import type { TooltipConfig, TooltipEngine } from "./types";
import { normalizeSelectors } from "./shared/selectors";
import { normalizeVertical, resolvePageVertical } from "./shared/verticals";

type InitFn = (config: TooltipConfig) => TooltipEngine;

const DEFAULT_EDGE_ORIGIN = "https://anchor.widgetbay.3labs.it";
const DEFAULT_LOCALE = "it-IT";
const DEFAULT_SELECTOR_PROFILE = "article";
const DEFAULT_IDLE_TIMEOUT_MS = 1200;
const SESSION_ID_KEY = "pt-session-id-v1";
const initializedScripts = new WeakSet<HTMLScriptElement>();

const CRAWLER_UA_PATTERN =
  /\b(bot|crawler|spider|slurp|archiver|googlebot|bingbot|duckduckbot|yandexbot|baiduspider|semrushbot|ahrefsbot|mj12bot)\b/i;

interface AutoInitResolvedConfig {
  selectors: string[];
  rulesUrl: string;
  resolveUrl: string;
  eventsUrl?: string;
  customerAmazonTag?: string;
  tenantId: string;
  locale: string;
  selectorProfile: string;
  vertical?: string;
  maxTotalMatches?: number;
  maxMatchesPerKeyword?: number;
  maxMatchesPerParagraph?: number;
  openInNewTab: boolean;
  disableOnBots: boolean;
  idleTimeoutMs: number;
}

export function maybeAutoInitFromScript(initFn: InitFn): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const script = findAutoInitScript();
  if (!script) {
    return;
  }

  if (initializedScripts.has(script)) {
    return;
  }

  const resolved = resolveAutoInitConfig(script, window.location.hostname);
  if (!resolved) {
    return;
  }

  if (resolved.disableOnBots && isLikelyCrawlerUserAgent(readUserAgent())) {
    return;
  }

  initializedScripts.add(script);

  runWhenDomReady(() => {
    runWhenIdle(
      () => {
        initFn(buildTooltipConfig(resolved));
      },
      resolved.idleTimeoutMs
    );
  });
}

export function isLikelyCrawlerUserAgent(userAgent: string): boolean {
  return CRAWLER_UA_PATTERN.test(userAgent);
}

export function resolveAutoInitConfig(
  script: HTMLScriptElement,
  hostname: string
): AutoInitResolvedConfig | null {
  const selectors = normalizeSelectors(parseCsv(script.dataset.ptSelectors));

  const origin = stripTrailingSlash(readOptionalUrl(script.dataset.ptOrigin) ?? DEFAULT_EDGE_ORIGIN);
  const rulesUrl = readOptionalUrl(script.dataset.ptRulesUrl) ?? `${origin}/v1/keyword-rules`;
  const resolveUrl = readOptionalUrl(script.dataset.ptResolveUrl) ?? `${origin}/v1/resolve-product`;
  const eventsEnabled = parseBoolean(script.dataset.ptEventsEnabled, true);
  const eventsUrl = eventsEnabled
    ? readOptionalUrl(script.dataset.ptEventsUrl) ?? `${origin}/v1/keyword-events/batch`
    : undefined;

  const tenantId = normalizeHostname(readOptionalString(script.dataset.ptTenantId) ?? hostname);
  const locale = readOptionalString(script.dataset.ptLocale) ?? DEFAULT_LOCALE;
  const selectorProfile = readOptionalString(script.dataset.ptSelectorProfile) ?? DEFAULT_SELECTOR_PROFILE;

  return {
    selectors,
    rulesUrl,
    resolveUrl,
    eventsUrl,
    customerAmazonTag: readOptionalString(script.dataset.ptAmazonTag),
    tenantId,
    locale,
    selectorProfile,
    vertical: normalizeVertical(readOptionalString(script.dataset.ptVertical)),
    maxTotalMatches: parseOptionalPositiveInteger(script.dataset.ptMaxTotalMatches),
    maxMatchesPerKeyword: parseOptionalPositiveInteger(script.dataset.ptMaxMatchesPerKeyword),
    maxMatchesPerParagraph: parseOptionalPositiveInteger(script.dataset.ptMaxMatchesPerParagraph),
    openInNewTab: parseBoolean(script.dataset.ptOpenInNewTab, true),
    disableOnBots: parseBoolean(script.dataset.ptDisableOnBots, true),
    idleTimeoutMs: parseOptionalPositiveInteger(script.dataset.ptIdleTimeoutMs) ?? DEFAULT_IDLE_TIMEOUT_MS
  };
}

function findAutoInitScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && hasTruthyAutoFlag(current.dataset.ptAutoInit)) {
    return current;
  }

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[data-pt-auto-init]"));
  for (let index = scripts.length - 1; index >= 0; index -= 1) {
    const script = scripts[index];
    if (script && hasTruthyAutoFlag(script.dataset.ptAutoInit)) {
      return script;
    }
  }

  return null;
}

function hasTruthyAutoFlag(value: string | undefined): boolean {
  return parseBoolean(value, false);
}

function buildTooltipConfig(resolved: AutoInitResolvedConfig): TooltipConfig {
  const sourceDomain = normalizeHostname(window.location.hostname);
  const pageVertical = resolveAutoInitVertical(window, document, resolved.vertical);
  const rulesCacheKey = `pt-rules-cache-${resolved.tenantId}-${resolved.locale}-${resolved.selectorProfile}-${pageVertical}`;

  return {
    selectors: resolved.selectors,
    rulesCacheKey,
    maxTotalMatches: resolved.maxTotalMatches,
    maxMatchesPerKeyword: resolved.maxMatchesPerKeyword,
    maxMatchesPerParagraph: resolved.maxMatchesPerParagraph,
    openInNewTab: resolved.openInNewTab,
    tenantId: resolved.tenantId,
    locale: resolved.locale,
    selectorProfile: resolved.selectorProfile,
    vertical: pageVertical,
    rulesProvider: async () => {
      const ifVersion = readCachedRulesVersion(rulesCacheKey);
      const url = buildScopedUrl(
        resolved.rulesUrl,
        {
          tenantId: resolved.tenantId,
          locale: resolved.locale,
          selectorProfile: resolved.selectorProfile,
          vertical: pageVertical
        },
        ifVersion
      );
      return requestJson(url, {
        method: "GET",
        headers: {
          "x-source-domain": sourceDomain
        }
      });
    },
    fetchProduct: async (context) => {
      return requestJson(resolved.resolveUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-source-domain": sourceDomain
        },
        body: JSON.stringify({
          ...context,
          sessionId: getOrCreateSessionId(),
          tenantId: resolved.tenantId,
          locale: resolved.locale,
          selectorProfile: resolved.selectorProfile,
          vertical: pageVertical,
          customerAmazonTag: resolved.customerAmazonTag
        })
      });
    },
    eventsReporter: resolved.eventsUrl
      ? async (events) => {
          const body = JSON.stringify({ events });
          const beaconSent = sendEventsViaBeacon(resolved.eventsUrl as string, body);
          if (beaconSent) {
            return;
          }

          await fetch(resolved.eventsUrl as string, {
            method: "POST",
            headers: {
              "content-type": "application/json",
              "x-source-domain": sourceDomain
            },
            body,
            keepalive: true,
            credentials: "omit"
          }).catch(() => undefined);
        }
      : undefined
  };
}

function buildScopedUrl(
  baseUrl: string,
  scope: { tenantId: string; locale: string; selectorProfile: string; vertical?: string },
  ifVersion?: string
): string {
  const url = new URL(baseUrl, window.location.href);
  url.searchParams.set("tenantId", scope.tenantId);
  url.searchParams.set("locale", scope.locale);
  url.searchParams.set("selectorProfile", scope.selectorProfile);
  if (scope.vertical) {
    url.searchParams.set("vertical", scope.vertical);
  }
  if (ifVersion) {
    url.searchParams.set("ifVersion", ifVersion);
  }
  return url.toString();
}

export function resolveAutoInitVertical(
  windowLike: Pick<Window, "location">,
  documentLike: Pick<Document, "title" | "body" | "querySelector" | "querySelectorAll">,
  explicitVertical?: string
): string {
  return resolvePageVertical({
    hostname: windowLike.location.hostname,
    explicitVertical,
    pathname: windowLike.location.pathname,
    title: documentLike.title,
    headings: Array.from(documentLike.querySelectorAll("h1, h2"))
      .map((element) => element.textContent ?? "")
      .filter((value) => value.trim().length > 0),
    bodyClassNames: documentLike.body?.className.split(/\s+/u).filter((value) => value.trim().length > 0) ?? [],
    metaKeywords: readMetaContent(documentLike, 'meta[name="keywords"]'),
    metaDescription: readMetaContent(documentLike, 'meta[name="description"]')
  }).vertical;
}

function readMetaContent(
  documentLike: Pick<Document, "querySelector">,
  selector: string
): string | undefined {
  const element = documentLike.querySelector(selector);
  if (!(element instanceof HTMLMetaElement)) {
    return undefined;
  }

  const content = element.content.trim();
  return content.length > 0 ? content : undefined;
}

function requestJson(url: string, init: RequestInit): Promise<any | null> {
  return fetch(url, init)
    .then(async (response) => {
      if (response.status === 304 || response.status === 204) {
        return null;
      }
      if (!response.ok) {
        return null;
      }
      return response.json();
    })
    .catch(() => null);
}

function readCachedRulesVersion(cacheKey: string): string | undefined {
  if (typeof window === "undefined" || !window.localStorage) {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as { version?: unknown; expiresAt?: unknown };
    if (typeof parsed.version !== "string" || !parsed.version.trim()) {
      return undefined;
    }

    if (typeof parsed.expiresAt !== "number" || parsed.expiresAt <= Date.now()) {
      return undefined;
    }

    return parsed.version;
  } catch {
    return undefined;
  }
}

function runWhenDomReady(task: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        task();
      },
      { once: true }
    );
    return;
  }

  task();
}

function runWhenIdle(task: () => void, timeoutMs: number): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(
      () => {
        task();
      },
      { timeout: timeoutMs }
    );
    return;
  }

  window.setTimeout(() => {
    task();
  }, 16);
}

function sendEventsViaBeacon(url: string, body: string): boolean {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return false;
  }

  if (!isSameOriginUrl(url)) {
    return false;
  }

  try {
    return navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } catch {
    return false;
  }
}

function isSameOriginUrl(url: string): boolean {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }

  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function getOrCreateSessionId(): string {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return createSessionId();
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) {
      return existing;
    }

    const next = createSessionId();
    window.sessionStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch {
    return createSessionId();
  }
}

function createSessionId(): string {
  return `pt-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function parseCsv(value: string | undefined): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function parseOptionalPositiveInteger(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (!value) {
    return fallback;
  }

  const normalized = value.trim().toLocaleLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }

  return fallback;
}

function normalizeHostname(value: string): string {
  const trimmed = value.trim().toLocaleLowerCase();
  if (!trimmed) {
    return "default";
  }
  return trimmed.startsWith("www.") ? trimmed.slice(4) : trimmed;
}

function readUserAgent(): string {
  if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") {
    return "";
  }
  return navigator.userAgent;
}

function readOptionalUrl(value: string | undefined): string | undefined {
  const candidate = readOptionalString(value);
  if (!candidate) {
    return undefined;
  }

  try {
    return new URL(candidate, window.location.href).toString();
  } catch {
    return undefined;
  }
}

function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.replace(/\/+$/, "") : value;
}

function readOptionalString(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
